import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  Badge
} from '@mui/material';
import {
  Sync as SyncIcon,
  SyncProblem as SyncProblemIcon,
  Delete as DeleteIcon,
  Refresh as RetryIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  ErrorOutline as ErrorIcon,
  CheckCircle as SuccessIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { db } from '../utils/db';
import { processQueue, refreshPendingCount } from '../services/syncService';
import { useOfflineStore } from '../stores/offlineStore';
import toast from 'react-hot-toast';

const ENTITY_LABELS = {
  closeCall: 'Close Call',
  closeCallCloseout: 'Close Call Closeout',
  goodCall: 'Good Call',
  inspection: 'Inspection',
  inspectionDraft: 'Inspection Draft',
  rams: 'RAMS',
  siteInduction: 'Site Induction',
  timesheet: 'Timesheet',
  incident: 'Incident',
  delivery: 'Delivery',
  assetRecord: 'Asset Register',
  assetDailyCheck: 'Asset Daily Check',
  assetFaultClose: 'Asset Fault Close-out'
};

export default function SyncManager() {
  const { pendingSyncCount, syncInProgress, isOnline } = useOfflineStore();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [failedItems, setFailedItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      // Get all unsynced items (pending + failed)
      const allItems = await db.syncQueue
        .filter(item => item.synced === 0 || item.synced === false)
        .toArray();
      
      const failed = allItems.filter(item => item.status === 'failed');
      const pending = allItems.filter(item => item.status !== 'failed');
      
      setItems(pending);
      setFailedItems(failed);
    } catch (err) {
      console.error('[SyncManager] Error loading items:', err);
    }
  }, []);

  // Refresh items when dialog opens
  useEffect(() => {
    if (open) {
      loadItems();
    }
  }, [open, loadItems, pendingSyncCount]);

  const handleRetryAll = async () => {
    if (!isOnline) {
      toast.error('Cannot retry while offline');
      return;
    }

    setLoading(true);
    try {
      // Reset failed items to pending
      for (const item of failedItems) {
        await db.syncQueue.update(item.id, {
          status: 'pending',
          retry_count: 0,
          last_error: null
        });
      }

      // Process the queue
      await processQueue();
      await loadItems();
      toast.success('Retry complete');
    } catch (err) {
      console.error('[SyncManager] Retry all failed:', err);
      toast.error('Retry failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRetrySingle = async (itemId) => {
    if (!isOnline) {
      toast.error('Cannot retry while offline');
      return;
    }

    setLoading(true);
    try {
      await db.syncQueue.update(itemId, {
        status: 'pending',
        retry_count: 0,
        last_error: null,
        synced: 0
      });
      await processQueue();
      await loadItems();
      toast.success('Item retried');
    } catch (err) {
      console.error('[SyncManager] Retry single failed:', err);
      toast.error('Retry failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDiscardSingle = async (itemId) => {
    try {
      // Remove from sync queue
      await db.syncQueue.delete(itemId);
      // Clean up associated files
      await db.files.where('sync_queue_id').equals(itemId).delete();
      await refreshPendingCount();
      await loadItems();
      toast.success('Item discarded');
    } catch (err) {
      console.error('[SyncManager] Discard failed:', err);
      toast.error('Failed to discard item');
    }
  };

  const handleDiscardAll = async () => {
    try {
      for (const item of failedItems) {
        await db.syncQueue.delete(item.id);
        await db.files.where('sync_queue_id').equals(item.id).delete();
      }
      await refreshPendingCount();
      await loadItems();
      toast.success('All failed items discarded');
    } catch (err) {
      console.error('[SyncManager] Discard all failed:', err);
      toast.error('Failed to discard items');
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    return date.toLocaleString();
  };

  // Don't show button if no pending items
  if (pendingSyncCount === 0) return null;

  return (
    <>
      {/* Floating sync status button */}
      <Tooltip title={`${pendingSyncCount} pending sync item${pendingSyncCount !== 1 ? 's' : ''}`}>
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 9998,
            bgcolor: failedItems.length > 0 ? '#d32f2f' : '#115740',
            color: '#fff',
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            '&:hover': {
              bgcolor: failedItems.length > 0 ? '#b71c1c' : '#0d4530'
            }
          }}
        >
          <Badge badgeContent={pendingSyncCount} color="warning" max={99}>
            {syncInProgress ? (
              <SyncIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
            ) : failedItems.length > 0 ? (
              <SyncProblemIcon />
            ) : (
              <CloudOffIcon />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Sync Manager Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncIcon />
          <Typography variant="h6" component="span">Sync Queue</Typography>
          <Box sx={{ ml: 'auto' }}>
            <IconButton onClick={() => setOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Summary */}
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<CloudOffIcon />}
              label={`${items.length} pending`}
              color="warning"
              size="small"
              variant="outlined"
            />
            {failedItems.length > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={`${failedItems.length} failed`}
                color="error"
                size="small"
                variant="outlined"
              />
            )}
            <Chip
              icon={isOnline ? <CloudDoneIcon /> : <CloudOffIcon />}
              label={isOnline ? 'Online' : 'Offline'}
              color={isOnline ? 'success' : 'default'}
              size="small"
            />
          </Box>

          {/* Failed Items */}
          {failedItems.length > 0 && (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                {failedItems.length} item{failedItems.length !== 1 ? 's' : ''} failed to sync after multiple retries.
              </Alert>

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#d32f2f' }}>
                Failed Items
              </Typography>
              <List dense sx={{ mb: 2 }}>
                {failedItems.map((item) => (
                  <ListItem
                    key={item.id}
                    sx={{
                      bgcolor: '#fff5f5',
                      borderRadius: 1,
                      mb: 0.5,
                      border: '1px solid #ffcdd2'
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ErrorIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ENTITY_LABELS[item.entity_type] || item.entity_type} - {item.operation}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(item.timestamp)}
                          </Typography>
                          {item.last_error && (
                            <Typography variant="caption" display="block" color="error">
                              Error: {item.last_error}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Retry">
                        <IconButton
                          size="small"
                          onClick={() => handleRetrySingle(item.id)}
                          disabled={loading || !isOnline}
                        >
                          <RetryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Discard">
                        <IconButton
                          size="small"
                          onClick={() => handleDiscardSingle(item.id)}
                          disabled={loading}
                          sx={{ color: '#d32f2f' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RetryIcon />}
                  onClick={handleRetryAll}
                  disabled={loading || !isOnline}
                  sx={{ bgcolor: '#115740', '&:hover': { bgcolor: '#0d4530' } }}
                >
                  Retry All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={handleDiscardAll}
                  disabled={loading}
                  color="error"
                >
                  Discard All Failed
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />
            </>
          )}

          {/* Pending Items */}
          {items.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Pending Items
              </Typography>
              <List dense>
                {items.map((item) => (
                  <ListItem
                    key={item.id}
                    sx={{
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      mb: 0.5
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CloudOffIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ENTITY_LABELS[item.entity_type] || item.entity_type} - {item.operation}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(item.timestamp)}
                          {item.retry_count > 0 && ` (${item.retry_count} retries)`}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Discard">
                        <IconButton
                          size="small"
                          onClick={() => handleDiscardSingle(item.id)}
                          disabled={loading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {items.length === 0 && failedItems.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <SuccessIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                All items synced successfully!
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          {syncInProgress && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto' }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Syncing...</Typography>
            </Box>
          )}
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
